import React, { useEffect } from 'react';
import { useEnterOpensMenu } from './useEnterOpensMenu';
import Select, { components } from 'react-select';
import {
  LogsFilterSortedDown,
  LogsFilterSortedUp,
  NavBarSortedDown,
  NavBarSortedUp,
} from '../ui/ProjectIcons';
import {
  activityLogStyle,
  routeFilterStyle,
  defaultStyle,
  languageSelectStyle,
  navLanguageMobileStyle,
  navTimezoneStyle,
  navTimezoneMobileStyle,
  navTimezoneRailStyle,
  timezoneStyle,
} from '../../../styles/componentstyles/selectinputstyles';

export type SelectVariant =
  | 'default'
  | 'language'
  | 'timezone'
  | 'activityLog'
  | 'routeFilter'
  | 'navTimezone'
  | 'navTimezoneMobile'
  | 'navTimezoneRail'
  | 'navLanguageMobile';

type SelectInputProps = {
  options: Array<any>;
  className: string;
  defaultValue?: object;
  value?: object | null;
  isDisabled: boolean;
  isLoading: boolean;
  isClearable: boolean;
  isSearchable: boolean;
  isMulti: boolean;
  name: string;
  onFocus?: () => void;
  onChange: Function;
  onMenuScrollToBottom?: any;
  handleInputChange?: any;
  variant?: SelectVariant;
  languageSelect?: boolean; // @deprecated Use variant="language" instead
  timezoneSelect?: boolean; // @deprecated Use variant="timezone" instead
  components?: any;
  onMenuOpen?: () => void;
  onMenuClose?: () => void;
  onKeyDown?: (e: React.KeyboardEvent) => void;
  noOptionsMessage?: (obj: { inputValue: string }) => React.ReactNode;
  autoSelectSingleOption?: boolean;
  menuPortalTarget?: HTMLElement | null;
  menuPosition?: 'fixed' | 'absolute';
  menuPlacement?: 'bottom' | 'top' | 'auto';
  inputId?: string;
  // Draws the field in its error state: a red line, and a red ring on focus.
  hasError?: boolean;
  formatOptionLabel?: (option: {
    label: string;
    value: string;
    [key: string]: any;
  }) => React.ReactNode;
};

const CustomOption = (props: any) => {
  const { data, innerRef, innerProps, isFocused } = props;
  // This bypasses the emotion `option` styles, so the keyboard-focus highlight
  // has to be applied here too — same color the styles (and hover) use.
  return (
    <div
      ref={innerRef}
      {...innerProps}
      className={`flex w-full flex-col whitespace-nowrap p-3 hover:bg-teal-t10 ${isFocused ? 'bg-teal-t10' : ''}`}
    >
      <div className="text-ui-text-primary-text">{data.label}</div>
      <div className="text-ui-text-primary-text">{data.subtitle}</div>
    </div>
  );
};

const CustomSingleValue = (props: any) => {
  const { data } = props;
  return (
    <components.SingleValue {...props}>
      <span>{data.label}</span>
    </components.SingleValue>
  );
};

const NavDropdownIndicator = (props: any) => {
  const { selectProps } = props;
  const className = 'text-ui-text-primary-text';

  return (
    <components.DropdownIndicator {...props} className={className}>
      {selectProps.menuIsOpen ? <NavBarSortedUp /> : <NavBarSortedDown />}
    </components.DropdownIndicator>
  );
};

const DefaultDropdownIndicator = (props: any) => {
  const { selectProps } = props;

  return (
    <components.DropdownIndicator {...props}>
      {selectProps.menuIsOpen ? (
        <LogsFilterSortedUp />
      ) : (
        <LogsFilterSortedDown />
      )}
    </components.DropdownIndicator>
  );
};

const SelectInput = ({
  options,
  className,
  defaultValue,
  value,
  isDisabled,
  isLoading,
  isClearable,
  isSearchable,
  isMulti,
  name,
  onFocus,
  onChange,
  onMenuScrollToBottom,
  handleInputChange,
  variant = 'default',
  languageSelect,
  timezoneSelect,
  onKeyDown,
  noOptionsMessage,
  autoSelectSingleOption = false,
  menuPortalTarget: menuPortalTargetProp,
  menuPosition: menuPositionProp,
  menuPlacement: menuPlacementProp,
  inputId,
  hasError = false,
  formatOptionLabel,
}: SelectInputProps) => {
  // For backward compatibility
  if (languageSelect) variant = 'language';
  if (timezoneSelect) variant = 'timezone';

  const isSingleOption = autoSelectSingleOption && options.length === 1;

  // Enter-opens-menu keyboard convention, shared with every other
  // react-select surface (see useEnterOpensMenu for the full rationale).
  const { selectRef, handleKeyDown } = useEnterOpensMenu(
    isSearchable,
    onKeyDown,
  );

  useEffect(() => {
    // Compared by value: `value` is usually a fresh object, so comparing the
    // objects themselves always differed and re-announced an unchanged selection.
    if (isSingleOption && (value as any)?.value !== options[0].value) {
      onChange(options[0]);
    }
  }, [isSingleOption, options]);

  // Get styles based on variant
  const getStylesForVariant = () => {
    switch (variant) {
      case 'timezone':
        return timezoneStyle;
      case 'language':
        return languageSelectStyle;
      case 'activityLog':
        return activityLogStyle;
      case 'routeFilter':
        return routeFilterStyle;
      case 'navTimezone':
        return navTimezoneStyle;
      case 'navTimezoneMobile':
        return navTimezoneMobileStyle;
      case 'navTimezoneRail':
        return navTimezoneRailStyle;
      case 'navLanguageMobile':
        return navLanguageMobileStyle;
      default:
        return defaultStyle;
    }
  };

  const styles = getStylesForVariant();

  // Define custom components outside the getComponentsForVariant function
  const DefaultOption = (props: any) => {
    const { children, ...rest } = props;
    return <components.Option {...rest}>{children}</components.Option>;
  };

  const DefaultSingleValue = (props: any) => {
    const { children, ...rest } = props;
    return (
      <components.SingleValue {...rest}>{children}</components.SingleValue>
    );
  };

  // Get components based on variant
  const getComponentsForVariant = () => {
    const baseComponents: any = {};

    // Add DropdownIndicator for specific variants
    if (
      [
        'language',
        'timezone',
        'navTimezone',
        'navTimezoneMobile',
        'navTimezoneRail',
        'navLanguageMobile',
      ].includes(variant)
    ) {
      baseComponents.DropdownIndicator = NavDropdownIndicator;
    }

    // Add CustomOption and CustomSingleValue for specific variants
    if (
      [
        'timezone',
        'activityLog',
        'navTimezone',
        'navTimezoneMobile',
        'navTimezoneRail',
      ].includes(variant)
    ) {
      baseComponents.Option = CustomOption;
      baseComponents.SingleValue = CustomSingleValue;
    } else {
      // For variants that don't have custom option components
      baseComponents.Option = DefaultOption;
      baseComponents.SingleValue = DefaultSingleValue;
    }

    if (['activityLog'].includes(variant)) {
      baseComponents.DropdownIndicator = DefaultDropdownIndicator;
    }

    return baseComponents;
  };

  return (
    <Select
      placeholder=""
      inputId={inputId}
      options={options}
      className={className}
      defaultValue={defaultValue}
      value={isSingleOption ? options[0] : value}
      isDisabled={isDisabled || isSingleOption}
      isLoading={isLoading}
      isClearable={isClearable}
      isSearchable={isSearchable}
      isMulti={isMulti}
      name={name}
      onFocus={onFocus}
      onChange={(e) => onChange(e)}
      onMenuScrollToBottom={onMenuScrollToBottom}
      onInputChange={handleInputChange}
      styles={styles}
      components={getComponentsForVariant()}
      ref={selectRef}
      onKeyDown={handleKeyDown}
      noOptionsMessage={noOptionsMessage}
      formatOptionLabel={formatOptionLabel as any}
      aria-invalid={hasError || undefined}
      // Read by the style functions through state.selectProps.
      {...({ hasError } as any)}
      menuPortalTarget={
        menuPortalTargetProp !== undefined
          ? menuPortalTargetProp
          : variant === 'routeFilter' && typeof document !== 'undefined'
            ? document.body
            : undefined
      }
      menuPosition={
        menuPositionProp ?? (variant === 'routeFilter' ? 'fixed' : undefined)
      }
      menuPlacement={menuPlacementProp ?? 'bottom'}
      filterOption={(option: any, input) => {
        if (!input) return true;
        const lowerCaseInput = input.toLowerCase().trim();
        const searchableFields = [
          option.label.toLowerCase(),
          ...(option.data.searchTerms || []),
        ];

        return searchableFields.some(
          (field) =>
            field.includes(lowerCaseInput) ||
            lowerCaseInput
              .split(' ')
              .every((word: string) => field.includes(word)),
        );
      }}
    />
  );
};

export default SelectInput;
